/**
 * BillingDisclosureModal — required consent step before Stripe checkout
 *
 * Zephyr is enrolled in Google Play's Alternative Billing (Alternative-
 * Billing-Only program), which requires prominent disclosure -- before
 * payment -- that this subscription is NOT processed through Google Play:
 * it won't appear in Play Store's own Subscriptions list, and needs its
 * own cancellation path (the Cancel Subscription feature on the profile
 * page, not Play Store's subscription management).
 *
 * Shown on every startCheckout() call regardless of platform/region --
 * showing it to a browser or non-US/UK/EEA user is harmless, where
 * omitting it for an enrolled-region native user would be the actual
 * violation.
 */

const MODAL_STYLES = `
.billing-disclosure-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
}
.billing-disclosure-panel {
    width: 100%;
    max-width: 420px;
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    box-shadow: 0 8px 32px rgba(0,0,0,0.25);
}
.billing-disclosure-panel h3 {
    margin: 0 0 0.75rem;
    font-size: 1.1rem;
    color: #2c3e50;
}
.billing-disclosure-panel p {
    margin: 0 0 0.75rem;
    font-size: 0.9rem;
    color: #2c3e50;
    line-height: 1.5;
}
.billing-disclosure-panel ul {
    margin: 0 0 0.75rem;
    padding-left: 1.2rem;
    font-size: 0.9rem;
    color: #2c3e50;
    line-height: 1.6;
}
.billing-disclosure-support {
    font-size: 0.8rem;
    color: #666;
}
.billing-disclosure-support a {
    color: #1e88e5;
}
.billing-disclosure-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.25rem;
}
.billing-disclosure-actions button {
    flex: 1;
    padding: 0.7rem;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    border: none;
}
#billingDisclosureCancel {
    background: #f0f0f0;
    color: #2c3e50;
}
#billingDisclosureCancel:hover {
    background: #e0e0e0;
}
#billingDisclosureContinue {
    background: #F5A623;
    color: #1a1a2e;
}
#billingDisclosureContinue:hover {
    background: #e0961a;
}
`;

/**
 * Show the disclosure and wait for the user's choice.
 * @returns {Promise<boolean>} true if the user chose to continue to payment
 */
export function showBillingDisclosure() {
    return new Promise((resolve) => {
        injectStyles();

        const overlay = document.createElement('div');
        overlay.className = 'billing-disclosure-overlay';

        const panel = document.createElement('div');
        panel.className = 'billing-disclosure-panel';
        panel.onclick = (e) => e.stopPropagation();

        panel.innerHTML = `
            <h3>Before You Subscribe</h3>
            <p>Zephyr Pro is <strong>$5.99/month</strong>, billed directly through <strong>Stripe</strong> (our payment processor) — not through Google Play.</p>
            <ul>
                <li>This subscription will <strong>not</strong> appear in Google Play's Subscriptions list</li>
                <li>Manage or cancel it anytime from your Zephyr profile page</li>
                <li>Cancel anytime — takes effect at the end of your current billing period</li>
            </ul>
            <p class="billing-disclosure-support">Questions, or need to dispute a charge? Email <a href="mailto:support@zephyr.travel">support@zephyr.travel</a>.</p>
            <div class="billing-disclosure-actions">
                <button id="billingDisclosureCancel">Cancel</button>
                <button id="billingDisclosureContinue">Continue to Payment</button>
            </div>
        `;

        overlay.appendChild(panel);
        document.body.appendChild(overlay);

        const cleanup = (result) => {
            overlay.remove();
            resolve(result);
        };

        overlay.addEventListener('click', () => cleanup(false));
        panel.querySelector('#billingDisclosureCancel').addEventListener('click', () => cleanup(false));
        panel.querySelector('#billingDisclosureContinue').addEventListener('click', () => cleanup(true));
    });
}

function injectStyles() {
    if (document.getElementById('billing-disclosure-styles')) return;
    const style = document.createElement('style');
    style.id = 'billing-disclosure-styles';
    style.textContent = MODAL_STYLES;
    document.head.appendChild(style);
}
